import { useMemo, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import type { CodeBreakdownRow, EngineerLoadRow, PriorityQueueRow, Severity } from '../api/types'
import { useAcknowledge, useDashboardStats, type ExceptionsFilter } from '../hooks/useApi'
import { tokens } from '../theme'
import { EmptyState } from './EmptyState'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'

const sevColour: Record<Severity, string> = {
  critical: tokens.alert,
  high: '#E09935',
  warn: tokens.gold,
  info: tokens.textSec,
}

export function DashboardPanel({
  filter,
  onFilterChange,
  onSelectException,
}: {
  filter: ExceptionsFilter
  onFilterChange: (next: ExceptionsFilter) => void
  onSelectException: (id: string) => void
}) {
  const { data: raw, isLoading, isError, error } = useDashboardStats()

  // Defensive normalise — the backend now emits [] for empty lists (matched
  // fix in resources.go) but any older cached response might still carry
  // null, and this keeps .length / .map safe against future regressions.
  const data = useMemo(() => {
    if (!raw) return raw
    return {
      ...raw,
      priority_queue: raw.priority_queue ?? [],
      by_code:        raw.by_code ?? [],
      top_engineers:  raw.top_engineers ?? [],
    }
  }, [raw])

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    )
  }
  if (isError || !data) {
    return (
      <Alert severity="error" variant="outlined">
        Failed to load dashboard: {error instanceof Error ? error.message : 'unknown error'}
      </Alert>
    )
  }

  const selectedSeverities: Severity[] = (filter.severity ?? []) as Severity[]
  const selectedCodes = filter.code ?? []

  const toggleSeverity = (sev: Severity) => {
    const next: Severity[] = selectedSeverities.includes(sev)
      ? selectedSeverities.filter((s) => s !== sev)
      : [...selectedSeverities, sev]
    onFilterChange({ ...filter, severity: next.length ? next : undefined })
  }

  const toggleCode = (code: string) => {
    const next = selectedCodes.includes(code)
      ? selectedCodes.filter((c) => c !== code)
      : [...selectedCodes, code]
    onFilterChange({ ...filter, code: next.length ? next : undefined })
  }

  return (
    <Stack spacing={2}>
      <KpiStrip
        openTotal={data.open_total}
        critical={data.severity.critical}
        high={data.severity.high}
        warn={data.severity.warn}
        unackHighPlus={data.unacknowledged_high_plus}
        engineerActive={data.engineer_state.active}
        engineersAtRisk={
          data.engineer_state.offroute +
          data.engineer_state.overdue_checkin +
          data.engineer_state.idle
        }
        ageOver4h={data.by_age_minutes.over_4h}
        selectedSeverities={selectedSeverities}
        onToggleSeverity={toggleSeverity}
      />

      {data.priority_queue.length > 0 && (
        <PriorityQueue rows={data.priority_queue} onSelect={onSelectException} />
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <CodeBreakdown
            rows={data.by_code}
            selectedCodes={selectedCodes}
            onToggleCode={toggleCode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <EngineerWorkload rows={data.top_engineers} />
        </Grid>
      </Grid>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// KPI strip — 5 tiles. Severity tiles click-to-filter; the rest are signal-only.
// ---------------------------------------------------------------------------

function KpiStrip({
  openTotal,
  critical,
  high,
  warn,
  unackHighPlus,
  engineerActive,
  engineersAtRisk,
  ageOver4h,
  selectedSeverities,
  onToggleSeverity,
}: {
  openTotal: number
  critical: number
  high: number
  warn: number
  unackHighPlus: number
  engineerActive: number
  engineersAtRisk: number
  ageOver4h: number
  selectedSeverities: Severity[]
  onToggleSeverity: (s: Severity) => void
}) {
  return (
    <Grid container spacing={1.5}>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiTile
          label="Critical"
          value={critical}
          accent={tokens.alert}
          active={selectedSeverities.includes('critical')}
          emphasise={critical > 0}
          onClick={() => onToggleSeverity('critical')}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiTile
          label="High"
          value={high}
          accent="#E09935"
          active={selectedSeverities.includes('high')}
          emphasise={high > 0}
          onClick={() => onToggleSeverity('high')}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiTile
          label="Warn"
          value={warn}
          accent={tokens.gold}
          active={selectedSeverities.includes('warn')}
          onClick={() => onToggleSeverity('warn')}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiTile
          label="Unack high+"
          value={unackHighPlus}
          accent={tokens.indigo}
          footnote={`of ${critical + high} combined`}
          emphasise={unackHighPlus > 0 && critical + high > 0}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 2.4 }}>
        <KpiTile
          label="Open total"
          value={openTotal}
          accent={tokens.indigo}
          footnote={
            engineersAtRisk > 0
              ? `${engineersAtRisk} engineer(s) at risk · ${engineerActive} active`
              : `${engineerActive} engineers active · ${ageOver4h} over 4 h`
          }
        />
      </Grid>
    </Grid>
  )
}

function KpiTile({
  label,
  value,
  accent,
  footnote,
  active = false,
  emphasise = false,
  onClick,
}: {
  label: string
  value: number
  accent: string
  footnote?: string
  active?: boolean
  emphasise?: boolean
  onClick?: () => void
}) {
  const clickable = !!onClick
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      sx={{
        px: 2,
        py: 1.5,
        height: '100%',
        cursor: clickable ? 'pointer' : 'default',
        borderColor: active ? accent : 'divider',
        borderLeft: `4px solid ${accent}`,
        bgcolor: active ? `${accent}14` : 'background.paper',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': clickable ? { bgcolor: `${accent}14` } : undefined,
        '&:focus-visible': { outline: `2px solid ${tokens.gold}`, outlineOffset: -2 },
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1.1, color: emphasise ? accent : 'text.primary' }}>
        {value}
      </Typography>
      {footnote && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {footnote}
        </Typography>
      )}
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Priority queue — top 5 unacknowledged critical/high cards.
// Each card has an inline Acknowledge button.
// ---------------------------------------------------------------------------

function PriorityQueue({
  rows,
  onSelect,
}: {
  rows: PriorityQueueRow[]
  onSelect: (id: string) => void
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Priority queue
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          · unacknowledged high + critical, oldest first
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ overflowX: 'auto' }}>
        {rows.map((row) => (
          <PriorityCard key={row.exception_id} row={row} onSelect={onSelect} />
        ))}
      </Stack>
    </Paper>
  )
}

function PriorityCard({ row, onSelect }: { row: PriorityQueueRow; onSelect: (id: string) => void }) {
  const ack = useAcknowledge()
  const age = useMemo(() => formatAge(row.opened_at), [row.opened_at])

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: { xs: '100%', md: 240 },
        flex: 1,
        borderLeft: `4px solid ${sevColour[row.severity]}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <Chip
          label={row.severity}
          size="small"
          sx={{
            bgcolor: sevColour[row.severity],
            color: 'common.white',
            fontWeight: 700,
            textTransform: 'uppercase',
            height: 20,
            fontSize: 10,
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
          {row.code}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {age}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {row.subject_summary || row.subject_type}
      </Typography>
      <Tooltip title={row.suggested_action ?? ''} placement="bottom-start">
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 30,
          }}
        >
          {row.suggested_action || '—'}
        </Typography>
      </Tooltip>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        <Button
          size="small"
          variant="contained"
          disabled={ack.isPending}
          onClick={() => ack.mutate(row.exception_id)}
          sx={{ textTransform: 'none', flex: 1 }}
        >
          {ack.isPending ? 'Acknowledging…' : 'Acknowledge'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onSelect(row.exception_id)}
          sx={{ textTransform: 'none' }}
        >
          Open
        </Button>
      </Stack>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Code breakdown — horizontal stacked bar per code. Click row → filter.
// ---------------------------------------------------------------------------

function CodeBreakdown({
  rows,
  selectedCodes,
  onToggleCode,
}: {
  rows: CodeBreakdownRow[]
  selectedCodes: string[]
  onToggleCode: (code: string) => void
}) {
  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.total)), [rows])
  const empty = rows.length === 0
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        By exception code
      </Typography>
      {!empty && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
          Click a row to filter the list below.
        </Typography>
      )}
      {empty && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            title="No exceptions yet"
            hint="The queue is quiet. When data flows in, codes appear here ranked by severity mix."
            icon={<BarChartOutlinedIcon />}
            size="sm"
          />
        </Box>
      )}
      <Stack spacing={1}>
        {rows.map((r) => {
          const selected = selectedCodes.includes(r.code)
          return (
            <Box
              key={r.code}
              role="button"
              tabIndex={0}
              onClick={() => onToggleCode(r.code)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggleCode(r.code)
                }
              }}
              sx={{
                px: 1,
                py: 0.75,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: selected ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                '&:focus-visible': { outline: `2px solid ${tokens.gold}`, outlineOffset: -1 },
              }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 150, minWidth: 150 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {r.code}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {r.category}
                  </Typography>
                </Box>
                <StackedBar
                  total={r.total}
                  max={max}
                  segments={[
                    { value: r.critical, colour: sevColour.critical },
                    { value: r.high, colour: sevColour.high },
                    { value: r.warn, colour: sevColour.warn },
                    { value: r.info, colour: sevColour.info },
                  ]}
                />
                <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right', fontWeight: 600 }}>
                  {r.total}
                </Typography>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Paper>
  )
}

function StackedBar({
  total,
  max,
  segments,
}: {
  total: number
  max: number
  segments: Array<{ value: number; colour: string }>
}) {
  const widthPct = (total / max) * 100
  return (
    <Box sx={{ flex: 1, height: 16, position: 'relative', bgcolor: tokens.divider, borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', height: '100%', width: `${widthPct}%` }}>
        {segments.map((seg, i) =>
          seg.value > 0 ? (
            <Box
              key={i}
              sx={{
                flex: seg.value,
                bgcolor: seg.colour,
              }}
              aria-label={`segment ${i} value ${seg.value}`}
            />
          ) : null,
        )}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Engineer workload — every active engineer, descending by open-exception
// count. Supervisors read this to spot headroom (zero-exception engineers
// at the bottom) and hot-spots (top) for reassignment.
// ---------------------------------------------------------------------------

type EngineerSortKey = 'workload_desc' | 'workload_asc' | 'name_asc' | 'name_desc'

function EngineerWorkload({ rows }: { rows: EngineerLoadRow[] }) {
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState<EngineerSortKey>('workload_desc')

  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.open_exceptions)), [rows])
  const loaded = rows.filter((r) => r.open_exceptions > 0).length
  const headroom = rows.length - loaded
  const empty = rows.length === 0

  // All hooks run every render — no early returns before this useMemo, or
  // the auto-refetch flip from empty→non-empty triggers React #310
  // (rendered more hooks than during the previous render).
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? rows.filter(
          (r) =>
            r.display_name.toLowerCase().includes(q) ||
            r.external_ref.toLowerCase().includes(q) ||
            r.state.toLowerCase().includes(q),
        )
      : rows
    const sorted = [...filtered]
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'workload_desc':
          return b.open_exceptions - a.open_exceptions || a.display_name.localeCompare(b.display_name)
        case 'workload_asc':
          return a.open_exceptions - b.open_exceptions || a.display_name.localeCompare(b.display_name)
        case 'name_asc':
          return a.display_name.localeCompare(b.display_name)
        case 'name_desc':
          return b.display_name.localeCompare(a.display_name)
      }
    })
    return sorted
  }, [rows, query, sortKey])

  if (empty) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
          Engineer workload
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            title="No engineers on the roster"
            hint="Once engineers are ingested through the WFM adapter, their workload lands here — heaviest first."
            icon={<PeopleAltOutlinedIcon />}
            size="sm"
          />
        </Box>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        Engineer workload
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {rows.length} engineer{rows.length === 1 ? '' : 's'} · {visibleRows.length} shown
        </Typography>
        {headroom > 0 && (
          <Chip
            label={`${headroom} with headroom`}
            size="small"
            sx={{ bgcolor: '#E8F5E9', color: '#1B5E20', height: 18, fontSize: 10 }}
          />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5, alignItems: { sm: 'center' } }}>
        <TextField
          size="small"
          placeholder="Search engineer…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    ⌕
                  </Typography>
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')} aria-label="clear search">
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ×
                    </Typography>
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortKey}
          onChange={(_, v) => {
            if (v) setSortKey(v as EngineerSortKey)
          }}
          aria-label="sort engineers"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1, py: 0.25, fontSize: 12 } }}
        >
          <ToggleButton value="workload_desc" aria-label="workload descending">
            <Tooltip title="Most exceptions first">
              <span>Load ↓</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="workload_asc" aria-label="workload ascending">
            <Tooltip title="Fewest exceptions first (headroom)">
              <span>Load ↑</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="name_asc" aria-label="name ascending">
            <Tooltip title="A → Z">
              <span>A-Z</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="name_desc" aria-label="name descending">
            <Tooltip title="Z → A">
              <span>Z-A</span>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {visibleRows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {query ? `No engineers match "${query}".` : 'No engineers on the roster.'}
        </Typography>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, maxHeight: 440, overflowY: 'auto', pr: 0.5 }}>
          <Stack spacing={1.25}>
            {visibleRows.map((r) => (
              <Box key={r.engineer_id}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }} noWrap>
                    {r.display_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {r.external_ref}
                  </Typography>
                  <Chip
                    label={r.state}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, minWidth: 32, textAlign: 'right', color: r.open_exceptions === 0 ? 'text.secondary' : 'text.primary' }}
                  >
                    {r.open_exceptions}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(r.open_exceptions / max) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: tokens.divider,
                    '& .MuiLinearProgress-bar': {
                      bgcolor:
                        r.critical > 0
                          ? tokens.alert
                          : r.high > 0
                            ? '#E09935'
                            : r.open_exceptions > 0
                              ? tokens.indigo
                              : '#22C55E',
                    },
                  }}
                />
                {(r.critical > 0 || r.high > 0) && (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
                    {r.critical > 0 && (
                      <Typography variant="caption" sx={{ color: tokens.alert, fontWeight: 600 }}>
                        {r.critical} critical
                      </Typography>
                    )}
                    {r.high > 0 && (
                      <Typography variant="caption" sx={{ color: '#E09935', fontWeight: 600 }}>
                        {r.high} high
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  )
}

function formatAge(iso: string): string {
  const then = new Date(iso).getTime()
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.round(hrs / 24)}d`
}
